(() => {
  if (customElements.get('ls-flyer-game')) return;

  const W = 480;
  const H = 640;
  const INK = '#1f1f21';
  const YELLOW = '#e3fc02';
  const ORANGE = '#e8690c';
  const MINT = '#7ef0b0';
  const PINK = '#ff5fa2';
  const SKY = '#4cc9f0';
  const FLOOR = 56;
  const SPEEDS = { slow: 0.8, normal: 1, fast: 1.25 };

  class LsFlyerGame extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.canvas = this.querySelector('[data-board]');
      this.ctx = this.canvas.getContext('2d');
      this.overlay = this.querySelector('[data-overlay]');
      this.overlayTitle = this.querySelector('[data-overlay-title]');
      this.overlayText = this.querySelector('[data-overlay-text]');
      this.startBtn = this.querySelector('[data-start]');
      this.scoreEl = this.querySelector('[data-score]');
      this.bestEl = this.querySelector('[data-best]');
      this.statusEl = this.querySelector('[data-status]');
      this.speedMul = SPEEDS[this.dataset.speed] || 1;
      this.best = 0;
      try { this.best = Number(localStorage.getItem('lsFlyerBest')) || 0; } catch (e) {}

      this.face = this.loadImage(this.dataset.player);
      this.bg = this.loadImage(this.dataset.background);

      this.state = 'idle';
      this.reset();
      this.loop = this.loop.bind(this);

      this.startBtn.addEventListener('click', () => this.start());
      this.canvas.addEventListener('pointerdown', (e) => {
        if (this.state !== 'playing') return;
        e.preventDefault();
        this.flap();
      });
      this.addEventListener('keydown', (e) => {
        if (this.state !== 'playing') return;
        if (e.key === ' ' || e.key === 'ArrowUp' || e.key === 'w' || e.key === 'W') {
          e.preventDefault();
          this.flap();
        }
      });

      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(this.canvas.parentElement);
      this.visObserver = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting && this.state === 'playing') this.end();
      });
      this.visObserver.observe(this);
      this.resize();
      this.updateStats();
      this.idleRaf = requestAnimationFrame((t) => this.idle(t));
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.raf);
      cancelAnimationFrame(this.idleRaf);
      this.resizeObserver && this.resizeObserver.disconnect();
      this.visObserver && this.visObserver.disconnect();
      this._ready = false;
    }

    loadImage(src) {
      if (!src) return null;
      const img = new Image();
      img.decoding = 'async';
      img.src = src;
      img.onload = () => this.draw(performance.now());
      return img;
    }

    reset() {
      this.y = H * 0.42;
      this.vy = 0;
      this.x = W * 0.3;
      this.r = this.dataset.style === 'sprite' ? (Number(this.dataset.size) || 110) * 0.24 : 30;
      this.pipes = [];
      this.score = 0;
      this.distance = 0;
      this.spawnIn = 0;
      this.flapT = 0;
      this.scroll = 0;
    }

    resize() {
      const dpr = window.devicePixelRatio || 1;
      const w = this.canvas.parentElement.clientWidth;
      if (!w) return;
      this.canvas.width = Math.round(w * dpr);
      this.canvas.height = Math.round(w * dpr * (H / W));
      this.scale = this.canvas.width / W;
      this.draw(performance.now());
    }

    start() {
      cancelAnimationFrame(this.idleRaf);
      this.reset();
      this.state = 'playing';
      this.overlay.hidden = true;
      this.updateStats();
      this.canvas.focus({ preventScroll: true });
      this.flap();
      this.last = performance.now();
      cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(this.loop);
    }

    flap() {
      this.vy = -430;
      this.flapT = 1;
    }

    end() {
      this.state = 'over';
      cancelAnimationFrame(this.raf);
      if (this.score > this.best) {
        this.best = this.score;
        try { localStorage.setItem('lsFlyerBest', String(this.best)); } catch (e) {}
      }
      this.updateStats();
      this.draw(performance.now());
      const text = (this.dataset.finalText || '').replace('[score]', this.score);
      this.overlayTitle.textContent = this.dataset.overTitle;
      this.overlayText.textContent = text;
      this.overlayText.hidden = false;
      this.startBtn.textContent = this.dataset.againLabel;
      this.overlay.hidden = false;
      this.startBtn.focus({ preventScroll: true });
      if (this.statusEl) this.statusEl.textContent = this.dataset.overTitle + ' ' + text;
    }

    idle(now) {
      if (this.state !== 'idle') return;
      this.y = H * 0.42 + Math.sin(now / 350) * 10;
      this.flapT = (Math.sin(now / 120) + 1) / 2;
      this.draw(now);
      this.idleRaf = requestAnimationFrame((t) => this.idle(t));
    }

    loop(now) {
      const dt = Math.min(0.033, (now - this.last) / 1000);
      this.last = now;
      const level = Math.min(this.score, 40);
      const speed = (170 + level * 3) * this.speedMul;
      const gap = Math.max(150, 200 - level * 1.5);

      this.vy += 1300 * dt;
      this.y += this.vy * dt;
      this.flapT = Math.max(0, this.flapT - dt * 4);
      this.scroll += speed * dt;

      this.spawnIn -= speed * dt;
      if (this.spawnIn <= 0) {
        const margin = 70;
        const top = margin + Math.random() * (H - FLOOR - gap - margin * 2);
        this.pipes.push({ x: W + 10, w: 76, top, gap, passed: false, hue: Math.floor(Math.random() * 4) });
        this.spawnIn = 250;
      }

      for (const p of this.pipes) {
        p.x -= speed * dt;
        if (!p.passed && p.x + p.w < this.x - this.r) {
          p.passed = true;
          this.score++;
          this.updateStats();
        }
      }
      this.pipes = this.pipes.filter((p) => p.x + p.w > -20);

      if (this.y + this.r >= H - FLOOR || this.y - this.r < -40 || this.hit()) {
        this.y = Math.min(this.y, H - FLOOR - this.r);
        this.end();
        return;
      }
      this.draw(now);
      this.raf = requestAnimationFrame(this.loop);
    }

    hit() {
      const r = this.r * 0.85;
      const circleRect = (rx, ry, rw, rh) => {
        const nx = Math.max(rx, Math.min(this.x, rx + rw));
        const ny = Math.max(ry, Math.min(this.y, ry + rh));
        const dx = this.x - nx;
        const dy = this.y - ny;
        return dx * dx + dy * dy < r * r;
      };
      return this.pipes.some((p) => {
        const bottomY = p.top + p.gap;
        return circleRect(p.x - 8, -100, p.w + 16, p.top + 100) || circleRect(p.x - 8, bottomY, p.w + 16, H - bottomY);
      });
    }

    updateStats() {
      this.scoreEl.textContent = this.score;
      this.bestEl.textContent = Math.max(this.best, this.score);
    }

    draw(now) {
      const ctx = this.ctx;
      if (!this.scale) return;
      ctx.setTransform(this.scale, 0, 0, this.scale, 0, 0);
      this.drawBackground(ctx);
      this.pipes.forEach((p) => this.drawPipe(ctx, p));
      this.drawFloor(ctx);
      this.drawPlayer(ctx, now);
    }

    drawBackground(ctx) {
      ctx.fillStyle = INK;
      ctx.fillRect(0, 0, W, H);
      const img = this.bg;
      if (img && img.complete && img.naturalWidth) {
        const s = Math.max((W * 1.4) / img.naturalWidth, H / img.naturalHeight);
        const iw = img.naturalWidth * s;
        const ih = img.naturalHeight * s;
        const travel = iw - W;
        const off = travel > 0 ? (this.scroll * 0.15) % (travel * 2) : 0;
        const x = -(off > travel ? travel * 2 - off : off);
        ctx.drawImage(img, x, (H - ih) / 2, iw, ih);
        ctx.fillStyle = 'rgba(31,31,33,0.55)';
        ctx.fillRect(0, 0, W, H);
      } else {
        ctx.strokeStyle = 'rgba(255,255,255,0.05)';
        ctx.lineWidth = 2;
        const off = (this.scroll * 0.3) % 60;
        for (let y = 0; y < H; y += 30) {
          ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
          const shift = (y / 30) % 2 ? 30 : 0;
          for (let x = -off + shift; x < W; x += 60) {
            ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x, y + 30); ctx.stroke();
          }
        }
      }
    }

    drawPipe(ctx, p) {
      const colors = [YELLOW, ORANGE, MINT, PINK];
      const fill = colors[p.hue];
      const bottomY = p.top + p.gap;
      const segs = [[0, p.top], [bottomY, H - FLOOR - bottomY]];
      ctx.lineWidth = 4;
      ctx.strokeStyle = INK;
      segs.forEach(([y, h], i) => {
        // film strip body
        ctx.fillStyle = '#f4f4f4';
        ctx.fillRect(p.x, y, p.w, h);
        ctx.strokeRect(p.x, y, p.w, h);
        // printed designs on the film
        const step = 46;
        for (let yy = i === 0 ? y + h - step - 22 : y + 26; i === 0 ? yy > y - step : yy < y + h; yy += i === 0 ? -step : step) {
          ctx.fillStyle = colors[(Math.abs(Math.round(yy / step)) + p.hue) % 4];
          ctx.beginPath();
          ctx.roundRect(p.x + 14, yy, p.w - 28, step - 14, 8);
          ctx.fill();
          ctx.lineWidth = 2.5;
          ctx.stroke();
        }
        ctx.lineWidth = 4;
        // heat press cap at the gap
        const capH = 26;
        const capY = i === 0 ? y + h - capH : y;
        ctx.fillStyle = fill;
        ctx.fillRect(p.x - 8, capY, p.w + 16, capH);
        ctx.strokeRect(p.x - 8, capY, p.w + 16, capH);
        ctx.fillStyle = INK;
        ctx.fillRect(p.x + 6, capY + capH / 2 - 2, p.w - 12, 4);
      });
    }

    drawFloor(ctx) {
      const y = H - FLOOR;
      ctx.fillStyle = YELLOW;
      ctx.fillRect(0, y, W, FLOOR);
      ctx.fillStyle = INK;
      ctx.fillRect(0, y, W, 4);
      const off = this.scroll % 40;
      for (let x = -off; x < W + 40; x += 40) {
        ctx.beginPath();
        ctx.moveTo(x, y + 4); ctx.lineTo(x + 20, y + 4); ctx.lineTo(x, y + FLOOR); ctx.lineTo(x - 20, y + FLOOR);
        ctx.closePath();
        ctx.fill();
      }
    }

    drawPlayer(ctx, now) {
      const r = this.r;
      const tilt = this.state === 'idle' ? 0 : Math.max(-0.5, Math.min(1.1, this.vy / 650));
      ctx.save();
      ctx.translate(this.x, this.y);
      const sprite = this.face;
      if (this.dataset.style === 'sprite' && sprite && sprite.complete && sprite.naturalWidth) {
        this.spriteW = Number(this.dataset.size) || 110;
        this.spriteH = this.spriteW * (sprite.naturalHeight / sprite.naturalWidth);
        ctx.rotate(tilt * 0.55);
        if (this.state === 'playing') {
          ctx.fillStyle = 'rgba(227,252,2,0.8)';
          for (let i = 1; i <= 4; i++) {
            ctx.beginPath();
            ctx.arc(-this.spriteW * 0.42 - i * 11, this.spriteH * 0.2 + Math.sin(now / 80 + i) * 4, 6 - i, 0, Math.PI * 2);
            ctx.fill();
          }
        }
        const squash = 1 + this.flapT * 0.06;
        const sw = this.spriteW * (2 - squash);
        const sh = this.spriteH * squash;
        ctx.drawImage(sprite, -sw / 2, -sh / 2, sw, sh);
        ctx.restore();
        return;
      }
      ctx.rotate(tilt);

      // wings
      const flapAngle = this.flapT * 0.9;
      [-1, 1].forEach((side) => {
        ctx.save();
        ctx.translate(-r * 0.2, side * 2);
        ctx.rotate(side === -1 ? -0.5 - flapAngle : 0.5 + flapAngle * 0.4);
        ctx.beginPath();
        ctx.ellipse(-r * 0.9, 0, r * 0.95, r * 0.38, 0, 0, Math.PI * 2);
        ctx.fillStyle = side === -1 ? '#ffffff' : '#e9e9e9';
        ctx.fill();
        ctx.lineWidth = 3;
        ctx.strokeStyle = INK;
        ctx.stroke();
        ctx.restore();
      });

      // spray trail
      if (this.state === 'playing') {
        ctx.fillStyle = 'rgba(227,252,2,0.75)';
        for (let i = 1; i <= 3; i++) {
          ctx.beginPath();
          ctx.arc(-r - i * 12, Math.sin(now / 80 + i) * 4, 5 - i, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      // sticker ring
      ctx.beginPath();
      ctx.arc(0, 0, r + 5, 0, Math.PI * 2);
      ctx.fillStyle = '#ffffff';
      ctx.fill();
      ctx.lineWidth = 3;
      ctx.strokeStyle = INK;
      ctx.stroke();

      const face = this.face;
      if (face && face.complete && face.naturalWidth) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(0, 0, r, 0, Math.PI * 2);
        ctx.clip();
        const zoom = Math.max(1, Number(this.dataset.zoom) || 1);
        const s = Math.max((r * 2) / face.naturalWidth, (r * 2) / face.naturalHeight) * zoom;
        const fw = face.naturalWidth * s;
        const fh = face.naturalHeight * s;
        const fx = Number(this.dataset.focusX || 50) / 100;
        const fy = Number(this.dataset.focusY || 50) / 100;
        ctx.drawImage(face, -fw * fx, -fh * fy, fw, fh);
        ctx.restore();
      } else {
        this.drawCan(ctx, r);
      }
      ctx.beginPath();
      ctx.arc(0, 0, r, 0, Math.PI * 2);
      ctx.lineWidth = 2;
      ctx.strokeStyle = INK;
      ctx.stroke();
      ctx.restore();
    }

    drawCan(ctx, r) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(0, 0, r, 0, Math.PI * 2);
      ctx.fillStyle = SKY;
      ctx.fill();
      ctx.clip();
      ctx.fillStyle = YELLOW;
      ctx.strokeStyle = INK;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.roundRect(-r * 0.45, -r * 0.45, r * 0.9, r * 1.4, 6);
      ctx.fill(); ctx.stroke();
      ctx.fillStyle = INK;
      ctx.fillRect(-r * 0.15, -r * 0.72, r * 0.3, r * 0.27);
      ctx.fillStyle = '#ffffff';
      ctx.beginPath(); ctx.arc(-r * 0.15, r * 0.05, r * 0.12, 0, Math.PI * 2); ctx.arc(r * 0.17, r * 0.05, r * 0.12, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = INK;
      ctx.beginPath(); ctx.arc(-r * 0.12, r * 0.07, r * 0.055, 0, Math.PI * 2); ctx.arc(r * 0.2, r * 0.07, r * 0.055, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
    }
  }

  customElements.define('ls-flyer-game', LsFlyerGame);
})();
